import React, { useCallback, useEffect, useState } from 'react';
import { Archive, Eye, Loader2, Pencil, RefreshCw, Send } from 'lucide-react';
import { API } from '../../config/apiBase';
import {
    PRIME_OPCOES, ROTA_API, STATUS_PEDIDO, corpoOferta, dataCurta, erroImagem, erroLink, fluxoAtivo, formDeOferta, podeCadastrar,
    rotuloPrime,
} from './amazonManual';

/**
 * /sistema/amazon-manual — oferta Amazon MANUAL com o link oficial do SiteStripe.
 *
 * Somente admin (a API também exige). Enquanto a Creators API não libera, a oferta Amazon não tem preço: o admin
 * informa título e o link do SiteStripe, e o site/WhatsApp levam DIRETO ao link oficial ("Ver oferta na Amazon"),
 * sem /r/ nem redirect do Super Vitrine. Nada aqui abre o link, consulta a Amazon ou publica sozinho: "Publicar
 * Amazon" aprova a mensagem EXATA do preview, e o envio real é ação operacional (publicar_amazon_manual.py --enviar).
 * Cliques: só os do site (beacon). Venda não é inferida: a fonte é o painel da Amazon Associados.
 */
const suave = { color: 'var(--muted)' };
const campo = { background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' };
const token = () => localStorage.getItem('token') || sessionStorage.getItem('token');
const VAZIO = { title: '', affiliate_link: '', image_url: '', category_id: '', campaign: '', requires_prime: 'unknown',
    note: '', tracking_attested: false };

async function chamar(caminho, opcoes = {}) {
    const r = await fetch(`${API}${ROTA_API}${caminho}`, {
        ...opcoes, headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
    });
    let corpo = null;
    try { corpo = await r.json(); } catch (_) { corpo = null; }
    if (!r.ok) throw new Error((corpo && corpo.detail) || `HTTP ${r.status}`);
    return corpo;
}

export function PreviewWhatsApp({ preview }) {
    if (!preview) return null;
    const { mensagem } = preview;
    return (
        <div className="space-y-2" data-testid="amz-preview">
            {mensagem.image_url ? <img src={mensagem.image_url} alt="" className="max-h-40 rounded" /> : null}
            <pre className="whitespace-pre-wrap text-xs rounded p-3" style={campo} data-testid="amz-preview-texto">{mensagem.caption}</pre>
            <p className="text-[11px]" style={suave} data-testid="amz-preview-destino">
                O link da mensagem é o oficial do SiteStripe, direto, sem passar pelo Super Vitrine: <span className="break-all">{preview.link_oficial}</span>
            </p>
            {!preview.publicavel ? <p className="text-xs" style={{ color: 'var(--status-amber)' }}>Não publicável: {preview.motivo}</p> : null}
        </div>
    );
}

export function AvisoDesativado({ flag }) {
    return (
        <p role="status" className="text-xs" style={{ color: 'var(--status-amber)' }} data-testid="amz-desativado">
            Fluxo desativado ({flag || 'AMAZON_MANUAL_SITESTRIPE_ENABLED'}=false): cadastro, edição, preview e publicação estão
            bloqueados e a oferta manual não aparece no site.
        </p>
    );
}

export function ListaOfertas({ ofertas, onPreview, onArquivar, onEditar = () => {} }) {
    if (!ofertas.length) return <p className="text-xs" style={suave}>Nenhuma oferta manual ainda.</p>;
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-[11px]" style={{ color: 'var(--text)' }} data-testid="amz-lista">
                <thead><tr style={suave}><th className="text-left">Título</th><th className="text-left">Link SiteStripe</th><th>Campanha</th><th>Prime</th>
                    <th>Criada</th><th>Publicada</th><th>Cliques no site</th><th>Cliques WhatsApp</th><th className="text-left">Envio</th><th /></tr></thead>
                <tbody>{ofertas.map((o) => {
                    const pedido = (o.pedidos || [])[0];
                    const st = pedido ? STATUS_PEDIDO[pedido.status] : null;
                    return (
                        <tr key={o.deal_id} data-testid="amz-linha">
                            <td>{o.title}{o.status !== 'approved' ? <span className="sv-badge sv-badge-muted ml-1">{o.status}</span> : null}</td>
                            <td className="break-all">{o.affiliate_link}</td>
                            <td className="text-center">{o.campaign || '—'}</td>
                            <td className="text-center">{rotuloPrime(o.requires_prime)}</td>
                            <td className="text-center">{dataCurta(o.created_at)}</td>
                            <td className="text-center">{dataCurta(o.published_at)}</td>
                            <td className="text-center">{o.clicks_site}</td>
                            <td className="text-center" style={suave} title="O WhatsApp leva direto ao link oficial">não medido</td>
                            <td>{st ? <span className={`sv-badge ${st.cls}`}>{st.label}</span> : '—'}{pedido && pedido.erro ? ` ${pedido.erro}` : ''}</td>
                            <td className="whitespace-nowrap">
                                {o.status === 'approved' ? <>
                                    <button type="button" className="sv-btn-system text-xs inline-flex items-center gap-1" onClick={() => onPreview(o.deal_id)} data-testid="amz-btn-preview"><Eye size={12} /> Preview</button>
                                    <button type="button" className="sv-btn-system text-xs inline-flex items-center gap-1 ml-1" onClick={() => onEditar(o)} data-testid="amz-btn-editar"><Pencil size={12} /> Editar</button>
                                    <button type="button" className="sv-btn-system text-xs inline-flex items-center gap-1 ml-1" onClick={() => onArquivar(o.deal_id)}><Archive size={12} /> Tirar do site</button>
                                </> : null}
                            </td>
                        </tr>
                    );
                })}</tbody>
            </table>
            <p className="text-[11px] mt-2" style={suave}>Cliques no site: contados sem redirect. WhatsApp: o link é o oficial, direto, e o SV não mede. Vendas e itens enviados: só no painel da Amazon Associados; o SV não infere conversão.</p>
        </div>
    );
}

export default function AmazonManualPage() {
    const [config, setConfig] = useState(null);
    const [categorias, setCategorias] = useState([]);
    const [ofertas, setOfertas] = useState([]);
    const [form, setForm] = useState(VAZIO);
    const [ocupado, setOcupado] = useState('');
    const [aviso, setAviso] = useState('');
    const [criada, setCriada] = useState(null);
    const [preview, setPreview] = useState(null);
    const [confirmar, setConfirmar] = useState(false);
    const [editando, setEditando] = useState(null);

    const carregar = useCallback(async () => {
        try {
            const [c, l] = await Promise.all([chamar('/config'), chamar('/offers')]);
            setConfig(c);
            setOfertas(l.offers || []);
        } catch (e) { setAviso(e.message); }
    }, []);

    useEffect(() => {
        carregar();
        fetch(`${API}/categories`).then((r) => (r.ok ? r.json() : [])).then((d) => setCategorias(Array.isArray(d) ? d : [])).catch(() => {});
    }, [carregar]);

    const mudar = (k) => (e) => {
        const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        setForm((f) => ({ ...f, [k]: v }));
        setCriada(null);
    };

    const cadastrar = async () => {
        setOcupado('criar'); setAviso('');
        try {
            const deal = editando
                ? await chamar(`/offers/${editando}`, { method: 'PUT', body: JSON.stringify(corpoOferta(form)) })
                : await chamar('/offers', { method: 'POST', body: JSON.stringify(corpoOferta(form)) });
            setCriada({ ...deal, editada: !!editando }); setForm(VAZIO); setEditando(null); await carregar();
        } catch (e) { setAviso(e.message); }
        setOcupado('');
    };

    const editar = async (o) => {
        setAviso(''); setCriada(null); setPreview(null);
        try {
            const detalhe = await chamar(`/offers/${o.deal_id}`);
            setForm(formDeOferta(detalhe, detalhe.note)); setEditando(o.deal_id);
        } catch (e) { setAviso(e.message); }
    };

    const abrirPreview = async (dealId) => {
        setOcupado('preview'); setAviso('');
        try { setPreview(await chamar(`/offers/${dealId}/preview`)); } catch (e) { setAviso(e.message); }
        setOcupado('');
    };

    const publicar = async () => {
        setOcupado('publicar'); setAviso('');
        try {
            await chamar(`/offers/${preview.deal_id}/publish`, { method: 'POST', body: JSON.stringify({ hash: preview.hash }) });
            setConfirmar(false); setPreview(null); await carregar();
        } catch (e) { setAviso(e.message); }
        setOcupado('');
    };

    const arquivar = async (dealId) => {
        try { await chamar(`/offers/${dealId}/archive`, { method: 'POST', body: '{}' }); await carregar(); } catch (e) { setAviso(e.message); }
    };

    const ativo = fluxoAtivo(config);
    const eLink = form.affiliate_link ? erroLink(form.affiliate_link) : '';
    const eImg = erroImagem(form.image_url);

    return (
        <div className="space-y-4" data-testid="amazon-manual-page">
            {confirmar && preview ? <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
                <div role="dialog" aria-modal="true" aria-labelledby="amz-publicar-title" className="sv-card-system max-w-lg space-y-3" style={{ background: 'var(--surface)', color: 'var(--text)' }}>
                    <h2 id="amz-publicar-title" className="font-bold">Publicar esta oferta no grupo de ofertas do WhatsApp?</h2>
                    <PreviewWhatsApp preview={preview} />
                    <p className="text-[11px]" style={suave}>Vai exatamente esta mensagem. Fora do F1: não ocupa janela nem conta no teto. O envio real é uma ação operacional (publicar_amazon_manual.py --enviar): uma por vez, com 15 min entre publicações manuais e do último envio.</p>
                    {aviso ? <p role="alert" className="text-xs" style={{ color: 'var(--status-amber)' }}>{aviso}</p> : null}
                    <button type="button" className="sv-btn-system" disabled={ocupado === 'publicar'} onClick={() => setConfirmar(false)}>Cancelar</button>
                    <button type="button" autoFocus className="sv-btn-gold-system sv-btn-system ml-2" disabled={ocupado === 'publicar'} onClick={publicar} data-testid="amz-confirmar-publicar">
                        {ocupado === 'publicar' ? 'Aprovando...' : 'Publicar Amazon'}
                    </button>
                </div>
            </div> : null}

            <div className="sv-card-system text-xs space-y-1" style={{ color: 'var(--text)' }}>
                <b>Oferta Amazon manual — link oficial do SiteStripe</b>
                <p style={suave}>Sem preço até a Creators API liberar (exige 10 vendas qualificadas em 30 dias). Nada é buscado na Amazon:
                    gere o link na barra SiteStripe da amazon.com.br e cole aqui. O link sai exatamente como foi gerado e é o destino
                    direto no site e no WhatsApp (sem redirect do Super Vitrine). Sem Telegram por enquanto.</p>
                {config ? <p style={suave}>Tracking ID da loja: <b>{config.tracking_id || 'NÃO CONFIGURADO'}</b></p> : null}
                {config && !ativo ? <AvisoDesativado flag={config.flag} /> : null}
            </div>

            <div className="sv-card-system space-y-2" data-testid="amz-form">
                <b style={{ color: 'var(--text)' }}>{editando ? `Editar oferta ${editando}` : 'Nova oferta Amazon manual'}</b>
                {editando ? <p className="text-[11px]" style={suave}>Salvar cancela um envio aprovado e ainda não feito: a mensagem precisa de novo preview.</p> : null}
                <label className="block text-xs" style={suave}>Título *
                    <input value={form.title} onChange={mudar('title')} maxLength={200} className="w-full rounded px-2 py-1 text-sm mt-1" style={campo} data-testid="amz-titulo" />
                </label>
                <label className="block text-xs" style={suave}>Link oficial do SiteStripe *
                    <input value={form.affiliate_link} onChange={mudar('affiliate_link')} placeholder="https://link.amazon/..." className="w-full rounded px-2 py-1 text-sm font-mono mt-1" style={campo} data-testid="amz-link" />
                </label>
                {eLink ? <p className="text-[11px]" style={{ color: 'var(--status-amber)' }} data-testid="amz-erro-link">{eLink}</p> : null}
                <label className="block text-xs" style={suave}>Imagem (opcional, https; não use imagem hospedada pela Amazon)
                    <input value={form.image_url} onChange={mudar('image_url')} className="w-full rounded px-2 py-1 text-sm mt-1" style={campo} />
                </label>
                {eImg ? <p className="text-[11px]" style={{ color: 'var(--status-amber)' }}>{eImg}</p> : null}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <label className="text-xs" style={suave}>Categoria
                        <select value={form.category_id} onChange={mudar('category_id')} className="w-full rounded px-2 py-1 text-sm mt-1" style={campo}>
                            <option value="">—</option>
                            {categorias.map((c) => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
                        </select>
                    </label>
                    <label className="text-xs" style={suave}>Campanha
                        <select value={form.campaign} onChange={mudar('campaign')} className="w-full rounded px-2 py-1 text-sm mt-1" style={campo} data-testid="amz-campanha">
                            <option value="">Nenhuma</option>
                            {Object.entries((config && config.campanhas) || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                    </label>
                    <label className="text-xs" style={suave}>Só para membros Prime?
                        <select value={form.requires_prime} onChange={mudar('requires_prime')} className="w-full rounded px-2 py-1 text-sm mt-1" style={campo} data-testid="amz-prime">
                            {PRIME_OPCOES.map((p) => <option key={p.valor} value={p.valor}>{p.rotulo}</option>)}
                        </select>
                    </label>
                </div>
                <label className="block text-xs" style={suave}>Observação interna (não aparece no site)
                    <input value={form.note} onChange={mudar('note')} maxLength={500} className="w-full rounded px-2 py-1 text-sm mt-1" style={campo} />
                </label>
                <label className="flex items-center gap-2 text-xs" style={{ color: 'var(--text)' }}>
                    <input type="checkbox" checked={form.tracking_attested} onChange={mudar('tracking_attested')} data-testid="amz-atesto" />
                    Gerei este link no SiteStripe com o Tracking ID {config ? config.tracking_id : ''}
                </label>
                <p className="text-[11px]" style={suave}>Sem preço, preço anterior ou desconto: sem a API oficial a Amazon não permite exibir preço. A campanha não vira desconto do produto.</p>
                <button type="button" className="sv-btn-gold-system sv-btn-system text-sm" disabled={!ativo || !podeCadastrar(form) || ocupado === 'criar'} onClick={cadastrar} data-testid="amz-cadastrar">
                    {ocupado === 'criar' ? <Loader2 className="w-4 h-4 animate-spin" /> : (editando ? 'Salvar alterações' : 'Cadastrar oferta')}
                </button>
                {editando ? <button type="button" className="sv-btn-system text-sm ml-2" onClick={() => { setEditando(null); setForm(VAZIO); }}>Cancelar edição</button> : null}
                {criada ? <p className="text-sm" style={{ color: 'var(--status-green)' }} data-testid="amz-criada">Oferta {criada.editada ? 'atualizada' : 'criada'}: {criada.deal_id} (no site, sem preço, link direto).</p> : null}
                {aviso && !confirmar ? <p className="text-xs" style={{ color: 'var(--status-amber)' }} data-testid="amz-aviso">{aviso}</p> : null}
            </div>

            {preview && !confirmar ? (
                <div className="sv-card-system space-y-2">
                    <b style={{ color: 'var(--text)' }}>Preview da mensagem no WhatsApp</b>
                    <PreviewWhatsApp preview={preview} />
                    <button type="button" className="sv-btn-gold-system sv-btn-system text-sm inline-flex items-center gap-1" disabled={!preview.publicavel}
                        onClick={() => { setAviso(''); setConfirmar(true); }} data-testid="amz-publicar"><Send size={14} /> Publicar Amazon</button>
                    <button type="button" className="sv-btn-system text-sm ml-2" onClick={() => setPreview(null)}>Fechar</button>
                </div>
            ) : null}

            <div className="sv-card-system space-y-2">
                <div className="flex items-center justify-between">
                    <b style={{ color: 'var(--text)' }}>Ofertas manuais e tracking</b>
                    <button type="button" className="sv-btn-system text-xs flex items-center gap-1" onClick={carregar}><RefreshCw size={12} /> Atualizar</button>
                </div>
                <ListaOfertas ofertas={ofertas} onPreview={abrirPreview} onArquivar={arquivar} onEditar={editar} />
            </div>
        </div>
    );
}
